import { getDatabase, ref, get, set, update, remove, push } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { ComponentLifetime, CostUnit, EditForm, MaintenanceTemplate } from '../Pages/Förvaltning/TemplatesEditor/types';
import { PhotoModel } from '../types/protocols';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { tombstoneAttachmentDiff } from './fileDeletionApi';
import { recordActivity } from './activityApi';
import { buildingsUsingTemplate } from '../Helpers/maintenanceTemplates';
import { requireAdmin } from './adminGuard';
import {
    CostChangeContext,
    costHistoryUpdates,
    costSnapshotsEqual,
    snapshotLifetimeCost,
    snapshotTemplateCost
} from './costHistoryApi';

/**
 * API for managing maintenance templates.
 *
 * Templates are stored in a flat structure at config/maintenanceTemplates/{id}
 * with primaryComponentType field to indicate the component type they were created for.
 *
 * Template edits require admin privileges as they affect all components
 * using that template across all buildings.
 */
export class MaintenanceTemplateApi {
    private db = getDatabase();
    private storage = getStorage();
    private readonly TEMPLATES_PATH = 'config/maintenanceTemplates';
    private readonly ATTACHMENT_ENTITY_TYPE = 'maintenanceTemplateAttachment';

    private getCurrentUser() {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad');
        }
        return user;
    }

    /**
     * Verify that the current user is an admin or superadmin, the same answer
     * as the `config/maintenanceTemplates` rule. Template edits affect all
     * components using that template, so they require admin privileges.
     */
    private verifyAdmin(): Promise<string> {
        return requireAdmin(this.db, 'ändra mallkostnader');
    }

    private lifetimePath(componentType: string): string {
        return `config/buildComponentTypes/${componentType}/maintenance/lifetime`;
    }

    /** Lifetime as stored: numbers coerced, a missing or invalid cost left out (the DB rejects NaN/undefined). */
    private cleanLifetime(lifetime: ComponentLifetime): ComponentLifetime {
        const finite = (value: unknown): value is number =>
            value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
        return {
            expectedLifetimeYears: Number(lifetime.expectedLifetimeYears),
            ...(finite(lifetime.replacementCost) && { replacementCost: Number(lifetime.replacementCost) }),
            ...(lifetime.costUnit && { costUnit: lifetime.costUnit }),
            ...(lifetime.directionalLifetimes && lifetime.directionalLifetimes.length > 0 && {
                directionalLifetimes: lifetime.directionalLifetimes.map(dl => ({
                    direction: dl.direction,
                    expectedLifetimeYears: Number(dl.expectedLifetimeYears),
                    ...(finite(dl.replacementCost) && { replacementCost: Number(dl.replacementCost) })
                }))
            })
        };
    }

    /**
     * Validate cost value
     */
    private validateCost(cost: number): void {
        if (isNaN(cost) || cost < 0) {
            throw new Error('Kostnad får inte vara negativ');
        }
        if (cost > 100000000) {
            throw new Error('Kostnad är för hög (max 100 000 000 kr)');
        }
    }

    /**
     * Validate cost unit
     */
    private validateCostUnit(costUnit: string): asserts costUnit is CostUnit {
        const validUnits: CostUnit[] = ['per_sqm', 'per_unit', 'per_lm'];
        if (!validUnits.includes(costUnit as CostUnit)) {
            throw new Error('Ogiltig kostnadsenhet');
        }
    }

    /**
     * Build template data object from EditForm, excluding undefined optional fields.
     */
    private buildTemplateData(form: EditForm): Omit<MaintenanceTemplate, 'id'> {
        return {
            name: form.name,
            description: form.description || '',
            interval_type: form.interval_type,
            interval_value: Number(form.interval_value),
            cost: Number(form.cost),
            cost_unit: form.cost_unit,
            primaryComponentType: form.primaryComponentType,
            ...(form.directionalIntervals && form.directionalIntervals.length > 0 && {
                directionalIntervals: form.directionalIntervals
            }),
            ...(form.instructionalAttachments && Object.keys(form.instructionalAttachments).length > 0 && {
                instructionalAttachments: form.instructionalAttachments
            }),
            ...(form.buildingKey && { buildingKey: form.buildingKey })
        };
    }

    // ============================================================
    // Template CRUD Operations
    // ============================================================

    /**
     * Mint a template key before the template is saved, so instructional
     * attachments can upload straight to the template's own Storage path
     * (Storage objects are write-once — no shared temp/ folder).
     */
    newTemplateKey(): string {
        const key = push(ref(this.db, this.TEMPLATES_PATH)).key;
        if (!key) {
            throw new Error('Kunde inte skapa mall-ID');
        }
        return key;
    }

    /**
     * Upload an instructional attachment blob for a template. Each upload gets
     * a fresh push-key Storage path (Storage objects are write-once). The
     * caller adds the returned entry to the template's instructionalAttachments
     * map under `dbKey`.
     */
    async uploadInstructionalAttachment(
        componentTypeKey: string,
        templateKey: string,
        file: File
    ): Promise<{ dbKey: string; attachment: PhotoModel }> {
        const user = this.getCurrentUser();

        if (!componentTypeKey || !templateKey) {
            throw new Error('Mall-ID saknas');
        }

        const attachmentKey = push(ref(this.db, this.TEMPLATES_PATH)).key;
        if (!attachmentKey) {
            throw new Error('Kunde inte skapa nyckel för bilagan');
        }

        const storagePath = `componentTypes/${componentTypeKey}/maintenance/templates/${templateKey}/attachments/${attachmentKey}`;
        await uploadBytes(storageRef(this.storage, storagePath), file);

        const attachment: PhotoModel = {
            key: file.name,
            author: user.uid,
            date: formatDateForStorage(new Date()),
            width: 0,
            height: 0,
            storagePath,
            fileSize: file.size
        };

        return { dbKey: attachmentKey, attachment };
    }

    /**
     * Create a new maintenance template.
     * @param form - The template form data (must include primaryComponentType;
     *   `templateKey` carries the pre-minted key when attachments were uploaded
     *   before saving)
     * @returns The ID of the newly created template
     */
    async createTemplate(form: EditForm): Promise<string> {
        await this.verifyAdmin();

        if (!form.name || form.name.trim() === '') {
            throw new Error('Namn är obligatoriskt');
        }
        if (!form.primaryComponentType) {
            throw new Error('Komponenttyp saknas');
        }

        const newKey = form.templateKey || this.newTemplateKey();

        const templateData = this.buildTemplateData(form);
        await set(ref(this.db, `${this.TEMPLATES_PATH}/${newKey}`), templateData);
        recordActivity('maintenanceTemplate.created', { targetKey: newKey, label: form.name.trim() });

        return newKey;
    }

    /**
     * Update an existing maintenance template.
     * Requires admin privileges.
     * @param templateId - The ID of the template to update
     * @param form - The updated template form data
     * @param ctx - Where the change was made, for the cost history
     */
    async updateTemplate(templateId: string, form: EditForm, ctx: CostChangeContext): Promise<void> {
        const uid = await this.verifyAdmin();

        if (!templateId) {
            throw new Error('Mall-ID saknas');
        }
        if (!form.name || form.name.trim() === '') {
            throw new Error('Namn är obligatoriskt');
        }

        const templatePath = `${this.TEMPLATES_PATH}/${templateId}`;

        // Verify template exists
        const snapshot = await get(ref(this.db, templatePath));
        if (!snapshot.exists()) {
            throw new Error('Underhållsmallen hittades inte');
        }
        const existing = snapshot.val() as Partial<MaintenanceTemplate>;

        // The form never carries the archive fields; a whole-node write must keep them
        const templateData = {
            ...this.buildTemplateData(form),
            ...(existing.archivedAt && { archivedAt: existing.archivedAt }),
            ...(existing.archivedBy && { archivedBy: existing.archivedBy })
        };
        await update(ref(this.db), {
            [templatePath]: templateData,
            ...costHistoryUpdates(
                this.db, 'templates', templateId,
                snapshotTemplateCost(existing as MaintenanceTemplate), snapshotTemplateCost(templateData),
                ctx, uid
            )
        });
        recordActivity('maintenanceTemplate.updated', { buildingKey: ctx.buildingKey, targetKey: templateId, label: form.name.trim() });

        // Soft-delete blobs for attachments this update removed
        await tombstoneAttachmentDiff(
            this.ATTACHMENT_ENTITY_TYPE,
            existing.instructionalAttachments,
            templateData.instructionalAttachments
        );
    }

    /**
     * The buildings whose active plan or draft holds an activity of the template.
     * Deleting such a template archives it instead (see deleteTemplate).
     */
    async getTemplateUsage(templateId: string): Promise<string[]> {
        const [active, drafts] = await Promise.all([
            get(ref(this.db, 'maintenance/active')),
            get(ref(this.db, 'maintenance/drafts'))
        ]);
        return buildingsUsingTemplate(templateId, active.val(), drafts.val());
    }

    /**
     * Delete a maintenance template. A template that an active plan or a draft
     * still uses is archived instead: kept so those activities still resolve,
     * no longer offered for new use.
     * Requires admin privileges.
     * @param templateId - The ID of the template to delete
     * @returns What happened to the template
     */
    async deleteTemplate(templateId: string): Promise<'deleted' | 'archived'> {
        const uid = await this.verifyAdmin();

        if (!templateId) {
            throw new Error('Mall-ID saknas');
        }

        const templatePath = `${this.TEMPLATES_PATH}/${templateId}`;

        // Verify template exists
        const snapshot = await get(ref(this.db, templatePath));
        if (!snapshot.exists()) {
            throw new Error('Underhållsmallen hittades inte');
        }
        const existing = snapshot.val() as Partial<MaintenanceTemplate>;

        const usedBy = await this.getTemplateUsage(templateId);
        if (usedBy.length > 0) {
            await update(ref(this.db, templatePath), {
                archivedAt: formatDateForStorage(new Date()),
                archivedBy: uid
            });
            recordActivity('maintenanceTemplate.archived', { targetKey: templateId, label: existing.name });
            return 'archived';
        }

        await remove(ref(this.db, templatePath));
        recordActivity('maintenanceTemplate.deleted', { targetKey: templateId, label: existing.name });

        // Soft-delete the deleted template's attachment blobs
        await tombstoneAttachmentDiff(
            this.ATTACHMENT_ENTITY_TYPE,
            existing.instructionalAttachments,
            undefined
        );
        return 'deleted';
    }

    /**
     * Restore an archived template: offered for new use again.
     * Requires admin privileges.
     */
    async restoreTemplate(templateId: string): Promise<void> {
        await this.verifyAdmin();

        if (!templateId) {
            throw new Error('Mall-ID saknas');
        }

        const templatePath = `${this.TEMPLATES_PATH}/${templateId}`;
        const snapshot = await get(ref(this.db, templatePath));
        if (!snapshot.exists()) {
            throw new Error('Underhållsmallen hittades inte');
        }
        const existing = snapshot.val() as Partial<MaintenanceTemplate>;

        await update(ref(this.db, templatePath), { archivedAt: null, archivedBy: null });
        recordActivity('maintenanceTemplate.updated', { targetKey: templateId, label: existing.name });
    }

    /**
     * Get a single template by ID.
     * @param templateId - The ID of the template
     * @returns The template or null if not found
     */
    async getTemplate(templateId: string): Promise<MaintenanceTemplate | null> {
        if (!templateId) return null;

        const snapshot = await get(ref(this.db, `${this.TEMPLATES_PATH}/${templateId}`));
        if (!snapshot.exists()) return null;

        return {
            id: templateId,
            ...snapshot.val()
        } as MaintenanceTemplate;
    }

    // ============================================================
    // Cost Update Operations (existing)
    // ============================================================

    /**
     * Update the lifetime replacement cost for a component type.
     * This affects all components of this type that use template cost.
     *
     * Requires admin privileges.
     */
    async updateLifetimeCost(
        componentType: string,
        cost: number,
        costUnit: CostUnit,
        ctx: CostChangeContext
    ): Promise<void> {
        // Verify admin permission first
        const uid = await this.verifyAdmin();

        // Validate inputs
        if (!componentType) {
            throw new Error('Komponenttyp saknas');
        }
        this.validateCost(cost);
        this.validateCostUnit(costUnit);

        const lifetimePath = this.lifetimePath(componentType);
        const snapshot = await get(ref(this.db, lifetimePath));
        // A lifetime created here has nothing to log (only edits are logged)
        const history = snapshot.exists()
            ? costHistoryUpdates(
                this.db, 'lifetimes', componentType,
                snapshotLifetimeCost(snapshot.val() as ComponentLifetime),
                snapshotLifetimeCost({ ...(snapshot.val() as ComponentLifetime), replacementCost: cost, costUnit }),
                ctx, uid
            )
            : {};

        await update(ref(this.db), {
            [`${lifetimePath}/replacementCost`]: cost,
            [`${lifetimePath}/costUnit`]: costUnit,
            ...history
        });
        // Lifetimes are component type config, not templates
        recordActivity('settings.updated', { buildingKey: ctx.buildingKey, targetKey: componentType });
    }

    /**
     * Save a component type's whole lifetime (years, replacement cost, directions).
     * Changing the replacement cost requires admin and is logged; a save that keeps
     * the cost (e.g. only the years) is open to every user, as before.
     */
    async saveLifetime(
        componentType: string,
        lifetime: ComponentLifetime,
        ctx: CostChangeContext
    ): Promise<ComponentLifetime> {
        const user = this.getCurrentUser();
        if (!componentType) {
            throw new Error('Komponenttyp saknas');
        }

        const lifetimePath = this.lifetimePath(componentType);
        const cleaned = this.cleanLifetime(lifetime);
        const snapshot = await get(ref(this.db, lifetimePath));

        let history: Record<string, unknown> = {};
        if (snapshot.exists()) {
            const from = snapshotLifetimeCost(snapshot.val() as ComponentLifetime);
            const to = snapshotLifetimeCost(cleaned);
            if (!costSnapshotsEqual(from, to)) {
                await this.verifyAdmin();
                history = costHistoryUpdates(this.db, 'lifetimes', componentType, from, to, ctx, user.uid);
            }
        }

        await update(ref(this.db), { [lifetimePath]: cleaned, ...history });
        recordActivity('settings.updated', { buildingKey: ctx.buildingKey, targetKey: componentType });
        return cleaned;
    }

    /**
     * Update the cost for a maintenance activity template.
     * This affects all components of this type that use this activity with template cost.
     *
     * Requires admin privileges.
     */
    async updateActivityTemplateCost(
        componentType: string,
        templateId: string,
        cost: number,
        costUnit: CostUnit,
        ctx: CostChangeContext
    ): Promise<void> {
        // Verify admin permission first
        const uid = await this.verifyAdmin();

        // Validate inputs
        if (!templateId) {
            throw new Error('Mall-ID saknas');
        }
        this.validateCost(cost);
        this.validateCostUnit(costUnit);

        // Templates are stored in flat structure
        const templatePath = `config/maintenanceTemplates/${templateId}`;

        // Verify the template exists before updating
        const templateSnapshot = await get(ref(this.db, templatePath));
        if (!templateSnapshot.exists()) {
            throw new Error('Underhållsmallen hittades inte');
        }

        await update(ref(this.db), {
            [`${templatePath}/cost`]: cost,
            [`${templatePath}/cost_unit`]: costUnit,
            ...costHistoryUpdates(
                this.db, 'templates', templateId,
                snapshotTemplateCost(templateSnapshot.val() as MaintenanceTemplate),
                { cost, unit: costUnit },
                ctx, uid
            )
        });
        recordActivity('maintenanceTemplate.updated', {
            buildingKey: ctx.buildingKey,
            targetKey: templateId,
            label: (templateSnapshot.val() as Partial<MaintenanceTemplate>).name,
        });
    }

    /**
     * Copy templates to a target component type.
     * Templates are passed from the component (already loaded in context).
     * Writes directly to Firebase to avoid N+1 admin checks.
     * @param sourceTemplates - Templates to copy
     * @param targetTypeKey - Target component type key
     * @param existingTargetNames - Set of existing template names in target type (for collision detection)
     * @returns Number of templates copied
     */
    async copyTemplates(
        sourceTemplates: MaintenanceTemplate[],
        targetTypeKey: string,
        existingTargetNames: Set<string>
    ): Promise<number> {
        await this.verifyAdmin();

        const usedNames = new Set(existingTargetNames);

        for (const template of sourceTemplates) {
            // Collision handling with counter: "Name" -> "Name (kopia)" -> "Name (kopia 2)"
            const name = this.resolveNameCollision(template.name, usedNames);
            usedNames.add(name);

            // Write directly to Firebase (avoids N+1 admin checks from createTemplate)
            const newKey = push(ref(this.db, this.TEMPLATES_PATH)).key;
            if (!newKey) {
                throw new Error('Kunde inte skapa mall-ID');
            }

            const templateData: Omit<MaintenanceTemplate, 'id'> = {
                name,
                description: template.description || '',
                interval_type: template.interval_type,
                interval_value: template.interval_value,
                cost: template.cost,
                cost_unit: template.cost_unit,
                primaryComponentType: targetTypeKey,
                ...(template.directionalIntervals && template.directionalIntervals.length > 0 && {
                    directionalIntervals: template.directionalIntervals
                }),
                ...(template.instructionalAttachments && Object.keys(template.instructionalAttachments).length > 0 && {
                    instructionalAttachments: template.instructionalAttachments
                }),
                ...(template.buildingKey && { buildingKey: template.buildingKey })
            };

            await set(ref(this.db, `${this.TEMPLATES_PATH}/${newKey}`), templateData);
        }
        if (sourceTemplates.length > 0) {
            recordActivity('maintenanceTemplate.created', {});
        }

        return sourceTemplates.length;
    }

    /**
     * Resolve name collisions by appending "(kopia)", "(kopia 2)", etc.
     */
    private resolveNameCollision(baseName: string, existingNames: Set<string>): string {
        if (!existingNames.has(baseName)) {
            return baseName;
        }

        let suffix = 1;
        let candidateName = `${baseName} (kopia)`;
        while (existingNames.has(candidateName)) {
            suffix++;
            candidateName = `${baseName} (kopia ${suffix})`;
        }
        return candidateName;
    }

    /**
     * Get the current admin status for the logged-in user.
     * Useful for UI to determine if admin-only features should be shown.
     */
    async isCurrentUserAdmin(): Promise<boolean> {
        try {
            await this.verifyAdmin();
            return true;
        } catch {
            return false;
        }
    }
}

// Export singleton instance
export const maintenanceTemplateApi = new MaintenanceTemplateApi();
